import React from 'react';
import { NotaGradinite } from '../components/NotaGradinite';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useRoutesProvider } from '../../../providers/provider-hooks';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import PageWrap from '../../../components/auth/page-wrap';
import { BaraPasiInscriere } from '@/shared/components/BaraPasiInscriere';
import { getErrorMessage, getErrorStatus } from '../../../core/api/response-handler';
import { inregistreazaCodOtp } from '../api/otp.api';
import { profesorCodSchema, type ProfesorCodFormData } from '../validation/registration.schemas';
import { trackSignupFormFailed, trackSignupFormSubmitted } from '@/telemetry/posthog-events';

/**
 * Ecranul 1 din wizardul de inscriere OTP-first (p8, raspuns ECOTIC 2026-09-10):
 * Nume, Prenume, Email, Telefon -> cod pe email, fara parola.
 *
 * 🔴 Cont + cod, nu formular + parola: contul PROFESOR se creeaza dezactivat la
 * CEREREA codului (`OtpService.inregistreaza()`), nu la verificare. Verificarea
 * (`VerificareCodPage`, existenta) devine sesiune, exact ca la login-ul cu cod.
 *
 * ⚠️ Textul de sub titlu e STATIC, la fel ca pe `VerificareCodPage`: raspunsul
 * serverului e uniform (email nou / deja inscris si verificat / rol neeligibil
 * ies toate identic), deci ecranul nu are din ce sa deduca un mesaj diferit.
 *
 * ⚠️ `identificator` trimis mai departe la `VerificareCodPage` e mereu EMAILUL —
 * canalul telefon e comutabil doar din backend (`auth.otp.inregistrare.canal`),
 * frontendul trimite mereu `identificator=email` la verify.
 */
export default function InregistrareCodPage() {
    const { PATHS, PAGES } = useRoutesProvider();
    const navigate = useNavigate();
    const location = useLocation();
    const [seTrimite, setSeTrimite] = React.useState(false);
    const [eroare, setEroare] = React.useState<string | null>(null);

    // Emailul vine precompletat de pe «Înscriere» (`CerereCodPage`, „Nu există cont…" →
    // «Înregistrează școala nouă»): omul tocmai l-a scris, nu-l punem sa-l scrie a doua oara.
    // Citit ca `unknown`: `state` vine din istoric si poate fi orice.
    const emailPrecompletat = (location.state as { email?: unknown } | null)?.email;

    const form = useForm<ProfesorCodFormData>({
        resolver: zodResolver(profesorCodSchema),
        defaultValues: {
            nume: '',
            prenume: '',
            email: typeof emailPrecompletat === 'string' ? emailPrecompletat : '',
            telefon: '',
        },
        mode: 'onBlur',
    });

    async function trimite(date: ProfesorCodFormData) {
        if (seTrimite) return;
        setSeTrimite(true);
        setEroare(null);
        try {
            const mesaj = await inregistreazaCodOtp(date);
            trackSignupFormSubmitted('PROFESOR');
            navigate(PATHS[PAGES.VERIFICARE_COD], {
                state: { identificator: date.email, cerutLa: Date.now(), mesaj, flux: 'inregistrare', dateInregistrare: date }
            });
        } catch (err) {
            trackSignupFormFailed({ role: 'PROFESOR', errorType: 'server', httpStatus: getErrorStatus(err) });
            setEroare(getErrorMessage(err, 'Nu am putut trimite codul. Încearcă din nou.'));
        } finally {
            setSeTrimite(false);
        }
    }

    function laInvalid(erori: FieldErrors<ProfesorCodFormData>) {
        trackSignupFormFailed({ role: 'PROFESOR', errorType: 'client', errorFields: Object.keys(erori) });
    }

    return (
        <PageWrap>
            <Card className="mx-auto mb-4 w-full max-w-md">
                <BaraPasiInscriere pasCurent={1} />
                <CardHeader>
                    {/* A3, sedinta 18.09: pasul 1 spune ce se cere ACUM, nu unde duce fluxul.
                        «Înscrie-ți școala» la pasul 1 din 4 lasa impresia ca scoala se alege aici;
                        ea vine la pasul 2. Mentiunea codului ramane — e singura explicatie pentru
                        absenta parolei. */}
                    <CardTitle as="h1" className="font-heading">Introduceți datele personale</CardTitle>
                    <CardDescription>
                        Nu ai nevoie de parolă: îți trimitem un cod unic pe e-mail.
                    </CardDescription>
                    <NotaGradinite />
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(trimite, laInvalid)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="prenume"
                                render={({ field }) => (
                                    <FormItem className="form-field-row">
                                        <FormLabel className="form-label-bold">Prenume:</FormLabel>
                                        <div className="flex-1 space-y-0">
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="text"
                                                    autoComplete="given-name"
                                                    className="form-input-styled"
                                                    aria-required="true"
                                                />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </div>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="nume"
                                render={({ field }) => (
                                    <FormItem className="form-field-row">
                                        <FormLabel className="form-label-bold">Nume:</FormLabel>
                                        <div className="flex-1 space-y-0">
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="text"
                                                    autoComplete="family-name"
                                                    className="form-input-styled"
                                                    aria-required="true"
                                                />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </div>
                                    </FormItem>
                                )}
                            />

                            {/* type="email", obligatoriu -- nu doar text (P1-4, valul p7): un
                                selector care cauta type-ul rateaza tacut campul gresit. */}
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem className="form-field-row">
                                        <FormLabel className="form-label-bold">Email:</FormLabel>
                                        <div className="flex-1 space-y-0">
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="email"
                                                    autoComplete="email"
                                                    autoCapitalize="none"
                                                    autoCorrect="off"
                                                    spellCheck={false}
                                                    className="form-input-styled"
                                                    aria-required="true"
                                                />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </div>
                                    </FormItem>
                                )}
                            />

                            {/* type="tel", acelasi motiv ca la email. */}
                            <FormField
                                control={form.control}
                                name="telefon"
                                render={({ field }) => (
                                    <FormItem className="form-field-row">
                                        <FormLabel className="form-label-bold">Telefon:</FormLabel>
                                        <div className="flex-1 space-y-0">
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="tel"
                                                    autoComplete="tel"
                                                    placeholder="07xxxxxxxx"
                                                    className="form-input-styled"
                                                    aria-required="true"
                                                />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </div>
                                    </FormItem>
                                )}
                            />

                            {eroare && (
                                <p role="alert" data-eroare-otp className="rounded-lg border border-error bg-error/10 p-3 text-small text-error m-0">
                                    {eroare}
                                </p>
                            )}

                            {/* `aria-disabled`, nu `disabled`: acelasi motiv ca pe `CerereCodPage` --
                                `buttonVariants` are `disabled:opacity-50`, sub pragul de contrast. */}
                            <Button
                                type="submit"
                                size="lg"
                                aria-disabled={seTrimite}
                                className={'min-h-11 w-full ' + (seTrimite ? 'cursor-not-allowed bg-light text-dark hover:bg-light' : '')}
                            >
                                {seTrimite ? 'Se trimite…' : 'Trimite-mi codul'}
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </PageWrap>
    );
}
